import { CheckIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Box, Icon, Pressable, Text } from "@/components/atoms";

import { IMenuSelection } from "@/stores/selectionSort";
import { ifWeb } from "@/utils/platform";

/**
 * Props for an individual menu selection item
 */
export interface MenuItemProps {
  readonly menu: IMenuSelection;
  readonly index: number;
  readonly isSelected: boolean;
  readonly isSmallScreen: boolean;
  readonly onPress: () => void;
}

/**
 * Memoized menu item component to prevent re-renders when other items change
 * @param root0 - Component props
 * @param root0.menu - Menu selection data containing title and type
 * @param root0.index - Index of this item in the list for styling purposes
 * @param root0.isSelected - Whether this menu item is currently selected
 * @param root0.isSmallScreen - Whether to use small screen styling
 * @param root0.onPress - Pre-memoized callback from Container when item is pressed
 */
const MenuItem = memo(function MenuItem({
  menu,
  index,
  isSelected,
  isSmallScreen,
  onPress,
}: MenuItemProps) {
  return (
    <Pressable
      key={`menu-${index}`}
      className={`flex-row items-center gap-3 ${!isSmallScreen ? "px-3" : "px-4"} py-2 ${index !== 0 ? "border-t border-surface-muted" : ""}`}
      onPress={onPress}
    >
      <Box className="w-4">{isSelected && <Icon as={CheckIcon} />}</Box>
      <Box className="flex-1">
        {/* SE-6773: the web arm used to name an `md` size step, for which this
            theme has no fontSize token — so it compiled to nothing and the
            element rendered at the Text atom's own default (size "md" ->
            text-title-sm). Naming that default keeps the render identical. */}
        <Text
          variant="unstyled"
          className={`font-medium ${ifWeb("text-title-sm", "text-title")}`}
        >
          {menu.title}
        </Text>
      </Box>
    </Pressable>
  );
});

MenuItem.displayName = "MenuItem";

export default MenuItem;
